import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function useCircleBooking() {
  const queryClient = useQueryClient();

  const bookEvent = useMutation({
    mutationFn: async ({ eventId, quantity = 1 }: { eventId: string; quantity?: number }) => {
      const { data, error } = await supabase.functions.invoke("book-circle-event", {
        body: { eventId, quantity },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-event"] });
      queryClient.invalidateQueries({ queryKey: ["circle-event-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-circle-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Successfully booked! Check your bookings for details.");
    },
    onError: (error: Error) => {
      console.error("Error booking circle event:", error);
      toast.error(error.message || "Failed to book event");
    },
  });

  const cancelBooking = useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string }) => {
      const { data, error } = await supabase.functions.invoke("cancel-circle-booking", {
        body: { bookingId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-event"] });
      queryClient.invalidateQueries({ queryKey: ["circle-event-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-circle-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      
      if (data?.refunded) {
        toast.success(`Booking cancelled. ${data.credits_refunded} credit(s) refunded.`);
      } else {
        toast.success("Booking cancelled.");
      }
    },
    onError: (error: Error) => {
      console.error("Error cancelling booking:", error);
      toast.error(error.message || "Failed to cancel booking");
    },
  });

  const removeAttendee = useMutation({
    mutationFn: async ({ bookingId }: { bookingId: string }) => {
      const { data, error } = await supabase.functions.invoke("remove-circle-attendee", {
        body: { bookingId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["circle-events"] });
      queryClient.invalidateQueries({ queryKey: ["circle-event-bookings"] });
      queryClient.invalidateQueries({ queryKey: ["my-circle-bookings"] });
      
      if (data?.credits_refunded > 0) {
        toast.success(`Attendee removed. ${data.credits_refunded} credit${data.credits_refunded !== 1 ? "s" : ""} refunded.`);
      } else {
        toast.success("Attendee removed.");
      }
    },
    onError: (error: Error) => {
      console.error("Error removing attendee:", error);
      toast.error(error.message || "Failed to remove attendee");
    },
  });

  return {
    bookEvent,
    cancelBooking,
    removeAttendee,
  };
}
